import { useMemo } from 'react'
import type { ComponentType } from '@/types/schemas/workspace'
import { DEV_FLAGS, FLAGS, type DevFlagKey, type FlagKey } from '@/services/analytics/feature-flags'
import { useFeatureFlag } from '@/services/analytics/use-feature-flag'
import { useDevFlag } from '@/services/analytics/dev-flags'

/**
 * Panel types that ARE a flagged surface. A type listed here is not offered
 * (Add Panel menu, Panel Hub, New Layout, mobile tabs) while its rollout flag
 * is off, and a saved copy renders through `RolloutGate` instead of the panel.
 *
 * This is the rollout layer only. The plan gate for the same type lives in
 * GATED_COMPONENTS and runs after it (feature-flags.ts: flag first, plan second).
 */
export const PANEL_ROLLOUT_FLAGS: Partial<Record<ComponentType, FlagKey | DevFlagKey>> = {
  notebook: FLAGS.NOTEBOOK,
  // The dashboard Home view is built on Market Overwatch; one flag rolls out both.
  'market-overwatch': FLAGS.HOME,
  // Both sit inside the realtime build gate (REALTIME_ENABLED); the flag is the second key.
  'price-alerts': FLAGS.PRICE_ALERTS,
  'alert-windows': FLAGS.ALERT_WINDOWS,
  // Dev tool: a PostHog dev flag (off unless PostHog says on; dev-flags.ts).
  'scanner-parity': DEV_FLAGS.SCANNER_PARITY,
}

/** `types` minus every type whose rollout flag `isOn` reports off. */
export function rolledOutPanelTypes(
  types: ComponentType[],
  isOn: (flag: FlagKey | DevFlagKey) => boolean,
): ComponentType[] {
  return types.filter((type) => {
    const flag = PANEL_ROLLOUT_FLAGS[type]
    return flag == null || isOn(flag)
  })
}

/**
 * `types` filtered by the live rollout flags. Hooks cannot run in a loop, so
 * each flag in PANEL_ROLLOUT_FLAGS reads here by name; add a line per new one.
 * A loading flag reads as off, so a flagged type appears once flags land.
 */
export function useRolledOutPanelTypes(types: ComponentType[]): ComponentType[] {
  const notebookOn = useFeatureFlag(FLAGS.NOTEBOOK)
  const homeOn = useFeatureFlag(FLAGS.HOME)
  const priceAlertsOn = useFeatureFlag(FLAGS.PRICE_ALERTS)
  const alertWindowsOn = useFeatureFlag(FLAGS.ALERT_WINDOWS)
  const parityOn = useDevFlag(DEV_FLAGS.SCANNER_PARITY)
  return useMemo(() => {
    const flagOn: Partial<Record<FlagKey | DevFlagKey, boolean>> = {
      [FLAGS.NOTEBOOK]: notebookOn,
      [FLAGS.HOME]: homeOn,
      [FLAGS.PRICE_ALERTS]: priceAlertsOn,
      [FLAGS.ALERT_WINDOWS]: alertWindowsOn,
      [DEV_FLAGS.SCANNER_PARITY]: parityOn,
    }
    return rolledOutPanelTypes(types, (flag) => flagOn[flag] ?? false)
  }, [types, notebookOn, homeOn, priceAlertsOn, alertWindowsOn, parityOn])
}
